/**
 * Recently viewed products, kept in this browser only (localStorage, newest first, max MAX_ITEMS handles).
 *
 * <s-recently-viewed-track data-handle="…"></s-recently-viewed-track>
 *   On the product page: records the product as just viewed.
 *
 * <s-recently-viewed-grid data-root="/">
 *   <div data-rv-items></div>  <p data-rv-loading></p>  <div data-rv-empty hidden></div>
 *   <button data-rv-clear hidden></button>
 * </s-recently-viewed-grid>
 *   On the Recently Viewed page (templates/page.recently-viewed.liquid): loads each product's card from
 *   templates/product.wishlist-card.liquid (?view=wishlist-card), the same cards as the wishlist page.
 *   Products that no longer exist are dropped from the list.
 */
const STORAGE_KEY = "devicean-recently-viewed";
const MAX_ITEMS = 24;

function readRecentlyViewed() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === "string" && handle) : [];
  } catch {
    return [];
  }
}

function writeRecentlyViewed(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
  } catch {
    // Storage full or blocked (private mode): history simply does not persist.
  }
}

function addRecentlyViewed(handle) {
  if (!handle) return;
  writeRecentlyViewed([handle, ...readRecentlyViewed().filter((item) => item !== handle)]);
}

function removeRecentlyViewed(handle) {
  writeRecentlyViewed(readRecentlyViewed().filter((item) => item !== handle));
}

class SRecentlyViewedTrack extends HTMLElement {
  connectedCallback() {
    addRecentlyViewed(this.dataset.handle);
  }
}

class SRecentlyViewedGrid extends HTMLElement {
  connectedCallback() {
    this.items = this.querySelector("[data-rv-items]");
    this.empty = this.querySelector("[data-rv-empty]");
    this.loading = this.querySelector("[data-rv-loading]");
    this.clearButton = this.querySelector("[data-rv-clear]");
    this.root = (this.dataset.root || "/").replace(/\/?$/, "/");
    if (!this.items) return;

    this.controller = new AbortController();
    this.clearButton?.addEventListener(
      "click",
      () => {
        writeRecentlyViewed([]);
        this.items.innerHTML = "";
        this.showState();
      },
      { signal: this.controller.signal },
    );
    this.load();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  async load() {
    const handles = readRecentlyViewed();
    if (!handles.length) {
      this.showState();
      return;
    }

    const { signal } = this.controller;
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${this.root}products/${encodeURIComponent(handle)}?view=wishlist-card`, { signal });
          if (response.status === 404) {
            removeRecentlyViewed(handle);
            return "";
          }
          return response.ok ? await response.text() : "";
        } catch {
          return "";
        }
      }),
    );
    if (signal.aborted) return;

    this.items.innerHTML = results.join("");
    this.showState();
  }

  showState() {
    if (this.loading) this.loading.hidden = true;
    const hasCards = this.items.querySelector("[data-wishlist-card]") !== null;
    this.items.hidden = !hasCards;
    if (this.empty) this.empty.hidden = hasCards;
    if (this.clearButton) this.clearButton.hidden = !hasCards;
  }
}

if (!customElements.get("s-recently-viewed-track")) {
  customElements.define("s-recently-viewed-track", SRecentlyViewedTrack);
}
if (!customElements.get("s-recently-viewed-grid")) {
  customElements.define("s-recently-viewed-grid", SRecentlyViewedGrid);
}
